import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatPrice, resolvePhoto } from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { ListingForm } from "@/components/ListingForm";
import { PayoutCard } from "@/components/PayoutCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PlusCircle, PackageOpen, Pencil, Trash2, ShieldAlert, MessageSquare, Power, Package, ClipboardList, TrendingUp } from "lucide-react";

export default function SellerDashboard() {
  const { user, refreshUser } = useAuth();
  const brand = useBrand();
  const { t } = useT();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/listings/mine").then((r) => setListings(r.data));
  useEffect(() => { refreshUser(); load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useAutoRefresh(load, 15000);

  const openEdit = (l) => { setEditing(l); setFormOpen(true); };

  const remove = async (id) => {
    await api.delete(`/listings/${id}`);
    toast.success("Supprimé");
    load();
  };
  const toggle = async (id) => { await api.patch(`/listings/${id}/status`); load(); };

  return (
    <div className="container-page py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-muted-foreground text-sm">{t("dashboard.welcome")}, {user.name}</p>
          <h1 className="font-heading font-extrabold text-3xl text-secondary">{t("dashboard.sellerTitle")}</h1>
          <p className="text-muted-foreground mt-1">{t("dashboard.sellerSubtitle")}</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="seller-rst">
            <RstScoreBadge score={user.rst_score} tier={user.rst_tier} size="md" />
            <span className="text-xs text-muted-foreground">{t("rst.scoreHint")}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/vendeur/commandes")} data-testid="seller-orders-btn">
            <Package className="h-4 w-4 mr-2" /> {t("order.ordersReceived")}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/vendeur/demandes")} data-testid="seller-requests-btn">
            <ClipboardList className="h-4 w-4 mr-2" /> {t("request.sellerTitle")}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/mon-evolution")} data-testid="seller-progress-btn">
            <TrendingUp className="h-4 w-4 mr-2" /> {t("progress.view")}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/echanges")} data-testid="seller-messages-btn">
            <MessageSquare className="h-4 w-4 mr-2" /> {t("nav.messages")}
          </Button>
          <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={() => navigate("/publier")} disabled={!user.verified} data-testid="add-listing-btn">
            <PlusCircle className="h-4 w-4 mr-2" /> {t("dashboard.addListing")}
          </Button>
        </div>
      </div>

      {!user.verified && (
        <div className="rounded-2xl border border-accent/40 bg-accent/20 p-5 mb-8 flex items-start gap-3" data-testid="not-verified-banner">
          <ShieldAlert className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-heading font-semibold text-foreground flex items-center gap-2">
              {t("dashboard.notVerifiedTitle")}
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t("verify.toVerify")}</span>
            </p>
            <p className="text-sm text-muted-foreground mt-1">{t("verify.notice")}</p>
            <p className="text-sm text-foreground mt-2 font-medium">{t("verify.sendDocs")}</p>
            <div className="flex flex-wrap gap-2 mt-1.5 text-sm">
              <a href={`https://wa.me/${(brand.contact?.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-success/10 text-success px-3 py-1 font-medium" data-testid="verify-whatsapp">
                <MessageSquare className="h-4 w-4" /> {t("verify.whatsapp")}: {brand.contact?.whatsapp}
              </a>
              <a href={`mailto:${brand.contact?.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 font-medium" data-testid="verify-email">
                {t("verify.email")}: {brand.contact?.email}
              </a>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t("verify.private")}</p>
          </div>
        </div>
      )}

      <div className="mb-8"><PayoutCard /></div>

      {listings.length === 0 ? (
        <div className="text-center py-20 rounded-2xl bg-card border border-dashed border-border" data-testid="seller-empty">
          <PackageOpen className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">{t("dashboard.noListings")}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {listings.map((l) => (
            <div key={l.id} className="rounded-2xl bg-card border border-border p-4 flex flex-col sm:flex-row gap-4 items-start" data-testid={`seller-listing-${l.id}`}>
              <img src={resolvePhoto(l.image)} alt="" className="h-24 w-24 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-semibold text-secondary">{t(`categories.${l.category}`)} — {t(`states.${l.state}`)}</h3>
                  <Badge className={l.status === "active" ? "bg-secondary/10 text-secondary border-0" : l.status === "pending" ? "bg-accent/20 text-accent-foreground border-0" : l.status === "rejected" ? "bg-destructive/10 text-destructive border-0" : "bg-muted text-muted-foreground border-0"} data-testid={`listing-status-${l.id}`}>
                    {l.status === "active" ? t("dashboard.active") : l.status === "pending" ? t("verify.toVerify") : l.status === "rejected" ? t("admin.reject") : t("listing.paused")}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{l.location} · {l.quantity} · {l.avg_weight}</p>
                <p className="font-heading font-bold text-primary mt-1">{formatPrice(l.price, brand.currencySymbol)} <span className="text-xs text-muted-foreground font-normal">/ {l.price_unit}</span></p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => toggle(l.id)} data-testid={`toggle-${l.id}`}><Power className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(l)} data-testid={`edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-destructive" data-testid={`delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("common.delete")} ?</AlertDialogTitle>
                      <AlertDialogDescription>{t(`categories.${l.category}`)} — {t(`states.${l.state}`)}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(l.id)} className="bg-destructive">{t("common.delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <ListingForm open={formOpen} onOpenChange={setFormOpen} initial={editing} categories={categories} onSaved={load} />
    </div>
  );
}
